import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import WarningMessage from '../../components/utilities/WarningMessage';
import Tooltip from '../../components/molecules/Tooltip';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import { ALERT_LEVELS, CAP_DISPLAY_NAMES, HARSH_ALERT_LEVELS } from '../consts';
import { buildTooltip, formatAlertNumber } from './alertTooltipBuilders';
import css from './UsageAlert.css';

const UsageAlert = ({ alerts = [], translate = () => '' }) => {
    if (!alerts || alerts.length === 0) {
        return null;
    }

    // Group alerts by alert level
    const alertsByLevel = alerts.reduce((acc, alert) => {
        const level = alert.alertLevel;
        if (!acc[level]) {
            acc[level] = [];
        }
        acc[level].push(alert);
        return acc;
    }, {});

    const getWarningType = alertLevel => {
        if (HARSH_ALERT_LEVELS.includes(alertLevel)) {
            return 'error';
        }
        return 'warning';
    };

    const ALERT_MESSAGE_KEY_MAP = {
        [ALERT_LEVELS.BLOCKED]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_MESSAGE',
        [ALERT_LEVELS.HARSH_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.HARSH_MESSAGE',
        [ALERT_LEVELS.MODERATE_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_MESSAGE',
        [ALERT_LEVELS.GENTLE_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_MESSAGE',
    };

    const buildAlertMessage = (alertLevel, alertsForLevel) => {
        const messageKey = ALERT_MESSAGE_KEY_MAP[alertLevel] || 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_MESSAGE';

        const firstAlert = alertsForLevel[0];
        const requiredRunRate = formatAlertNumber(firstAlert.expectedRunRatePercent / 14);

        return (
            <>
                {alertsForLevel.map(alert => {
                    const { metric } = alert;
                    const metricName = String(translate(CAP_DISPLAY_NAMES[metric] || metric) || '');
                    const tooltip = buildTooltip(alertLevel, alert, translate);

                    return (
                        <div key={metric} className={css.alertMetricContainer}>
                            <div className={css.alertMetricHeader}>
                                <strong><Translate id={messageKey} /></strong>
                                {tooltip && (
                                    <Tooltip
                                        title={tooltip}
                                        interactive
                                        distance={15}
                                        position="top"
                                        wrapperClass={css.tooltipWrapper}
                                        contentClass={css.tooltipContent}
                                    >
                                        <Icon
                                            name="question-line"
                                            size={IconSizes.X_SMALL}
                                            className={css.tooltipIcon}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    );
                })}
                <div className={css.mitigationContainer}>
                    <div>{translate('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MITIGATION_TITLE')}</div>
                    <div>1. {translate('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MITIGATION_STEP_1')}</div>
                    <div>2. {translate('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MITIGATION_STEP_2', { requiredRunRate })}</div>
                </div>
                <div className={css.seeMoreLink}>
                    <Translate id="STATIC.PAGES.ACCOUNT_PLAN.ALERTS.SEE_MORE_LINK" />
                </div>
            </>
        );
    };

    return (
        <div className={css.alertsWrapper}>
            {Object.entries(alertsByLevel).map(([level, alertsForLevel]) => (
                <WarningMessage
                    key={level}
                    type={getWarningType(Number(level))}
                    message={buildAlertMessage(Number(level), alertsForLevel)}
                    showIcon={false}
                    showTypeIcon
                    showSpacer={false}
                    className={css.warningMessage}
                />
            ))}
        </div>
    );
};

UsageAlert.propTypes = {
    alerts: PropTypes.arrayOf(
        PropTypes.shape({
            metric: PropTypes.string.isRequired,
            alertLevel: PropTypes.number.isRequired,
            usagePercent: PropTypes.number.isRequired,
            runRatePercent: PropTypes.number,
            cap: PropTypes.number,
            expectedRunRatePercent: PropTypes.number,
            actualCapUsage: PropTypes.number,
        })
    ),
    translate: PropTypes.func,
};

export default UsageAlert;
